import React from 'react';
import { ActionIcon, Badge, Button, Indicator, rem } from '@mantine/core';
import { IconFilter } from '@tabler/icons-react';
import FilterPanel from './Filter.Panel';
import FilterSurface from './Filter.Surface';
import type { TypeDateSection, TypeFilterSection, TypeListSort } from '@/types/list/types.list.filter';

type Props = {
    sections: TypeFilterSection[];
    active: Record<string, string[]>;
    onToggle: (sectionId: string, value: string) => void;
    excluded?: Record<string, boolean>;
    onExclude?: (sectionId: string, value: boolean) => void;
    onClear: () => void;
    dates?: TypeDateSection[];
    dateValues?: Record<string, string>;
    onDateChange?: (sectionId: string, value: string) => void;
    sort?: TypeListSort;
    size?: 'input-sm' | 'input-xs';
    quiet?: boolean;
};

const FilterMenu: React.FC<Props> = ({
    sections, active, onToggle, excluded, onExclude, onClear, dates = [], dateValues = {}, onDateChange, sort, size = 'input-sm', quiet = false,
}) => {
    const [opened, setOpened] = React.useState(false);
    const activeCount = Object.values(active).reduce((sum, values) => sum + values.length, 0) + Object.keys(dateValues).length;
    const label = activeCount ? `Filtri, ${activeCount} attivi` : 'Filtri';
    const toggle = () => setOpened((current) => !current);

    const trigger = quiet ? (
        <Indicator disabled={!activeCount} label={activeCount} size={16} offset={4} color="brand" styles={{ indicator: { fontSize: rem(10), fontWeight: 700 } }}>
            <ActionIcon variant={activeCount ? 'light' : 'subtle'} color={activeCount ? 'brand' : 'gray'} size={size} onClick={toggle} aria-label={label} aria-expanded={opened}>
                <IconFilter size={17} stroke={1.8} />
            </ActionIcon>
        </Indicator>
    ) : (
        <Button
            variant={activeCount ? 'light' : 'default'}
            color={activeCount ? 'brand' : undefined}
            leftSection={<IconFilter size={16} stroke={1.8} />}
            rightSection={activeCount ? <Badge size="sm" circle color="brand" variant="filled">{activeCount}</Badge> : null}
            onClick={toggle}
            aria-label={label}
            aria-expanded={opened}
            aria-haspopup="dialog"
            style={{ flexShrink: 0 }}
        >
            Filtri
        </Button>
    );

    return (
        <FilterSurface opened={opened} onClose={() => setOpened(false)} target={trigger} label="Filtri" align="end">
            <FilterPanel
                sections={sections}
                active={active}
                onToggle={onToggle}
                excluded={excluded}
                onExclude={onExclude}
                onClear={onClear}
                dates={dates}
                dateValues={dateValues}
                onDateChange={onDateChange}
                sort={sort}
                activeCount={activeCount}
                onDone={() => setOpened(false)}
            />
        </FilterSurface>
    );
};

export default FilterMenu;
